import * as React from 'react';
import { IDeeplTranslationRequest, IDeeplTranslationResponse } from 'PyzShopUi/scripts/shop-ui/interfaces';
import { deeplTranslationService } from 'PyzShopUi/scripts/shop-ui/services/DeeplTranslationService';
import { useTranslationHook } from 'PyzShopUi/scripts/utils/translationHook';


export interface IUseDeeplTranslation {
    deeplTranslate(): void;
    cancelDeeplTranslate(): void;
    translatedSummary: string;
    translatedDescription: string;
    translatedComment: string;
    isLoading: boolean;
    isTranslated: boolean;
    isError: boolean;
    errorMessage: string;
}

interface ITranslatedObject {
    summary: string;
    description: string;
    comment: string;
}

function useDeeplTranslation(
    summary:string,
    description: string,
    comment: string,
    sourceLanguage: string,
): IUseDeeplTranslation {
    const cachedTranslatedRef = React.useRef<ITranslatedObject>();
    const { translate } = useTranslationHook();

    const [translatedObject, setTranslatedObject] = React.useState<ITranslatedObject>({
        summary,
        description,
        comment,
    });
    const [isTranslated, setIsTranslated] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const [isError, setIsError] = React.useState<boolean>(false);
    const errorMessageRef = React.useRef<string>(
        translate('Automatic translations are unfortunately not available currently.', { ns: 'article-review' })
    )

    const deeplTranslate = () => {
        setIsError(false);
        if (isLoading) {
            return;
        }

        if (cachedTranslatedRef.current) {
            const { summary, description, comment } = cachedTranslatedRef.current;
            setTranslatedObject({
                summary,
                description,
                comment
            });

            setIsTranslated(true);

            return;
        }

        setIsLoading(true);

        const request: IDeeplTranslationRequest = {
            sourceTitle: translatedObject.summary,
            sourceText: translatedObject.description,
            sourceComment: translatedObject.comment,
            sourceLanguage,
        };

        deeplTranslationService.translate(request).then((response: IDeeplTranslationResponse) => {
            setIsLoading(false);
            if (!response.isSuccess) {
                setIsError(true);

                return;
            }

            const { text, title, comment } = response.translatedData;

            cachedTranslatedRef.current = {
                summary: title,
                description: text,
                comment,
            }

            setTranslatedObject({
                summary: title,
                description: text,
                comment,
            });

            setIsTranslated(true);
        }).catch(() => {
            setIsError(true);
            setIsLoading(false);
        });
    }

    const cancelDeeplTranslate = () => {
        setTranslatedObject({
            summary,
            description,
            comment
        });

        setIsTranslated(false);
    }

    return {
        deeplTranslate,
        cancelDeeplTranslate,
        translatedSummary: translatedObject.summary,
        translatedDescription: translatedObject.description,
        translatedComment: translatedObject.comment,
        isLoading,
        isTranslated,
        isError,
        errorMessage: errorMessageRef.current
    };
}

export default useDeeplTranslation;
